import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useListParams } from '@/hooks/useListParams'
import {
  useTaxRates,
  useCreateTaxRate,
  useUpdateTaxRate,
  useDeleteTaxRate,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { TaxRate, TaxRateRequest } from '@/types'

const COLUMN_COUNT = 6

const cardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'

const drawerClass =
  'tf-right-drawer-short *:min-w-0 [&>button]:right-[18px] [&>button]:top-[18px] [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const fieldInputClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const dialogBtnOutlineClass =
  'rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'

const dialogBtnPrimaryClass =
  'rounded-xl bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'

const switchClass =
  'h-6 w-10 border-0 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#8f95a6] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

const thClass =
  'h-10 border-b border-[#e7e9ef] bg-[#fbfbfc] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.02em] text-[#3d434e]'

const tdClass =
  'h-16 border-b border-[#e7e9ef] px-3 py-2 align-middle text-sm text-[#171a20]'

const actionBtnClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionBtnClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionBtnClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
interface FormState {
  name: string
  rate: string
  is_default: boolean
  is_active: boolean
}

const EMPTY: FormState = {
  name: '',
  rate: '',
  is_default: false,
  is_active: true,
}

function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string
  required?: boolean
  children: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className='text-xs font-medium leading-4 text-[#171a20]'
    >
      {children}
      {required && <span className='text-[#d63b33]'> *</span>}
    </Label>
  )
}

function StatusTag({ active }: { active: boolean }) {
  const { t } = useTranslation('settings')

  return (
    <span
      className='inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={
        active
          ? { backgroundColor: '#D1FAE5', color: '#065F46' }
          : { backgroundColor: '#e6e6e6', color: '#1e1e1e' }
      }
    >
      {active ? t('common.active') : t('common.inactive')}
    </span>
  )
}

function RateCell({ value }: { value: number }) {
  return (
    <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
      {value}%
    </span>
  )
}

// 2.11.8 — Tax rate configuration. Paginated CRUD list; one default rate.
export function TaxRatesPanel() {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useTaxRates(query)
  const create = useCreateTaxRate()
  const update = useUpdateTaxRate()
  const remove = useDeleteTaxRate()

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<TaxRate | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [toDelete, setToDelete] = useState<TaxRate | null>(null)

  const rows = useMemo(() => data?.data ?? [], [data])
  const set = (patch: Partial<FormState>) =>
    setForm(current => ({ ...current, ...patch }))

  function openCreate() {
    setEditing(null)
    setForm(EMPTY)
    setErrors({})
    setOpen(true)
  }

  function openEdit(rate: TaxRate) {
    setEditing(rate)
    setForm({
      name: rate.name,
      rate: String(rate.rate),
      is_default: rate.is_default,
      is_active: rate.is_active,
    })
    setErrors({})
    setOpen(true)
  }

  function validate(): TaxRateRequest | null {
    const next: Record<string, string> = {}

    if (!form.name.trim()) next.name = t('taxRates.validation.nameRequired')

    const rate = Number(form.rate)
    if (form.rate === '' || Number.isNaN(rate) || rate < 0 || rate > 100) {
      next.rate = t('taxRates.validation.rateRange')
    }

    setErrors(next)
    if (Object.keys(next).length > 0) return null

    return {
      name: form.name.trim(),
      rate,
      is_default: form.is_default,
      is_active: form.is_active,
    }
  }

  function submit() {
    const body = validate()
    if (!body) return

    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing
          ? t('taxRates.toast.updated')
          : t('taxRates.toast.created'),
      })
      setOpen(false)
    }

    const onError = (error: unknown) =>
      toast({ variant: 'destructive', title: getApiErrorMessage(error) })

    if (editing) {
      update.mutate({ id: editing.id, body }, { onSuccess, onError })
      return
    }

    create.mutate(body, { onSuccess, onError })
  }

  function toggleActive(rate: TaxRate) {
    update.mutate(
      {
        id: rate.id,
        body: {
          name: rate.name,
          rate: rate.rate,
          is_default: rate.is_default,
          is_active: !rate.is_active,
        },
      },
      {
        onSuccess: () =>
          toast({
            variant: 'success',
            title: rate.is_active
              ? t('taxRates.toast.deactivated')
              : t('taxRates.toast.activated'),
          }),
        onError: error =>
          toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
      }
    )
  }

  function confirmDelete() {
    if (!toDelete) return

    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('taxRates.toast.deleted') })
        setToDelete(null)
      },
      onError: error =>
        toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
    })
  }

  return (
    <>
      <section className={cardClass}>
        <header className='flex items-center gap-2 overflow-hidden pb-2 pt-6 pr-6'>
          <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
            <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
              {t('taxRates.title')}
            </h2>
          </div>
          <Button
            className='rounded-xl bg-[#ff5a1f] px-4 py-2.5 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'
            onClick={openCreate}
          >
            <Plus className='mr-2 h-4 w-4' />
            {t('taxRates.addTaxRate')}
          </Button>
        </header>

        <div className='flex flex-col gap-4 px-6 pb-4 pt-4'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[720px] caption-bottom text-sm'>
              <TableHeader>
                <TableRow className='border-0'>
                  <TableHead className={cn(thClass, 'w-[320px]')}>
                    {t('common.name')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[140px] text-center')}>
                    {t('taxRates.columns.rate')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[160px] text-center')}>
                    {t('common.status')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[180px]')}>
                    {t('taxRates.columns.lastUpdated')}
                  </TableHead>
                  <TableHead className={cn(thClass, 'w-[136px] text-right')}>
                    <span>{t('common.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading &&
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                        <Skeleton className='h-6 w-full' />
                      </TableCell>
                    </TableRow>
                  ))}

                {isError && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-destructive'
                      )}
                    >
                      {t('taxRates.loadError')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading && !isError && rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      className={cn(
                        tdClass,
                        'py-10 text-center text-muted-foreground'
                      )}
                    >
                      {t('taxRates.empty')}
                    </TableCell>
                  </TableRow>
                )}

                {!isLoading &&
                  !isError &&
                  rows.map(rate => {
                    return (
                      <TableRow key={rate.id} className='group border-0'>
                        <TableCell className={cn(tdClass)}>
                          <span className='text-sm font-medium leading-[1.4] text-[#171a20]'>
                            {rate.name}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <RateCell value={rate.rate} />
                        </TableCell>
                        <TableCell className={cn(tdClass, 'text-center')}>
                          <StatusTag active={rate.is_active} />
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <span className='whitespace-nowrap text-sm leading-[1.4] text-[#171a20]'>
                            {formatDate(rate.updated_at)}
                          </span>
                        </TableCell>
                        <TableCell className={cn(tdClass)}>
                          <div className='flex items-center justify-end gap-2'>
                            <TruncatedTextTooltip
                              content={
                                rate.is_active
                                  ? t('taxRates.actions.deactivate')
                                  : t('taxRates.actions.activate')
                              }
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  accentActionBtnClass
                                )}
                                aria-label={
                                  rate.is_active
                                    ? t('taxRates.actions.deactivate')
                                    : t('taxRates.actions.activate')
                                }
                                disabled={update.isPending}
                                onClick={() => toggleActive(rate)}
                              >
                                {rate.is_active ? (
                                  <EyeOff className='size-4' />
                                ) : (
                                  <Eye className='size-4' />
                                )}
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.edit')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  accentActionBtnClass
                                )}
                                aria-label={t('common:actions.edit')}
                                onClick={() => openEdit(rate)}
                              >
                                <Pencil className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                            <TruncatedTextTooltip
                              content={t('common:actions.delete')}
                              delayDuration={0}
                              alwaysShow
                              closeOnPointerDown
                              className='inline-flex shrink-0'
                            >
                              <button
                                type='button'
                                className={cn(
                                  actionBtnClass,
                                  destructiveActionBtnClass
                                )}
                                aria-label={t('common:actions.delete')}
                                onClick={() => setToDelete(rate)}
                              >
                                <Trash2 className='size-4' />
                              </button>
                            </TruncatedTextTooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </table>
          </div>

          <ListPagination
            metadata={data?.metadata}
            page={page}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
            showSummary={false}
          />
        </div>
      </section>

      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next && (create.isPending || update.isPending)) return
          setOpen(next)
        }}
      >
        <DialogContent
          side='right'
          showCloseButton={false}
          className={drawerClass}
        >
          <SettingsDrawerHeader
            title={
              editing
                ? t('taxRates.dialog.editTitle')
                : t('taxRates.dialog.addTitle')
            }
            closeDisabled={create.isPending || update.isPending}
            titleClassName='text-base'
          />

          <div className='flex flex-1 flex-col overflow-y-auto bg-[#f4f5f8]'>
            <div className='flex flex-1 flex-col gap-4 px-6 py-6'>
              <div className={cardClass}>
                <div className='border-b border-transparent px-0 pb-2 pt-4'>
                  <div className='flex items-center border-l-4 border-[#ff5a1f] px-4'>
                    <h3 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
                      INFORMATION
                    </h3>
                  </div>
                </div>

                <div
                  key={editing?.id ?? 'create'}
                  className='flex flex-col gap-6 px-4 pb-6 pt-4'
                >
                  <div className='flex flex-col gap-1'>
                    <FieldLabel htmlFor='tr-name' required>
                      {t('taxRates.fields.name')}
                    </FieldLabel>
                    <Input
                      id='tr-name'
                      value={form.name}
                      placeholder={t('taxRates.fields.enterHere')}
                      onChange={event => {
                        set({ name: event.target.value })
                        setErrors(current => ({ ...current, name: '' }))
                      }}
                      className={cn(
                        fieldInputClass,
                        errors.name && 'border border-destructive'
                      )}
                    />
                    {errors.name && (
                      <p className='text-sm text-destructive'>{errors.name}</p>
                    )}
                  </div>

                  <div className='flex flex-col gap-1'>
                    <FieldLabel htmlFor='tr-rate' required>
                      {t('taxRates.fields.rate')}
                    </FieldLabel>
                    <Input
                      id='tr-rate'
                      type='number'
                      step='0.01'
                      min='0'
                      max='100'
                      value={form.rate}
                      placeholder={t('taxRates.fields.enterHere')}
                      onChange={event => {
                        set({ rate: event.target.value })
                        setErrors(current => ({ ...current, rate: '' }))
                      }}
                      className={cn(
                        fieldInputClass,
                        errors.rate && 'border border-destructive'
                      )}
                    />
                    {errors.rate && (
                      <p className='text-sm text-destructive'>{errors.rate}</p>
                    )}
                  </div>

                  <div>
                    <div className='flex items-center gap-3'>
                      <Label
                        htmlFor='tr-active'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('taxRates.fields.active')}
                      </Label>
                      <Switch
                        id='tr-active'
                        checked={form.is_active}
                        onCheckedChange={value => set({ is_active: value })}
                        className={switchClass}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='tf-right-drawer-footer sticky bottom-0 justify-end'>
              <Button
                type='button'
                variant='outline'
                className={dialogBtnOutlineClass}
                onClick={() => setOpen(false)}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                type='button'
                className={dialogBtnPrimaryClass}
                onClick={submit}
                disabled={create.isPending || update.isPending}
              >
                {t('common:actions.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={isOpen =>
          !remove.isPending && !isOpen && setToDelete(null)
        }
        title={t('taxRates.dialog.deleteTitle')}
        description={t('taxRates.dialog.deleteMessage')}
        confirmLabel={t('taxRates.dialog.deleteConfirm')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </>
  )
}

export default TaxRatesPanel
